import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FiX } from "react-icons/fi";
import {
  CreateNewsletterInput,
  Newsletter,
  NewsletterCadence,
  newsletterAdminService,
  PreviewResult,
} from "../../services/newsletterAdminService";
import HtmlPreviewPane from "./HtmlPreviewPane";
import { useAuth } from "../../contexts/AuthContext";

const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;

interface NewsletterFormProps {
  initial?: Newsletter;
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateNewsletterInput) => void;
}

interface FormState {
  name: string;
  description: string;
  cadence: NewsletterCadence;
  custom_cadence_days: string;
  send_day_of_week: string;
  send_hour_utc: string;
  llm_system_prompt: string;
  subject_template: string;
  topic_batch_size: string;
  topic_refill_threshold: string;
}

const EMPTY: FormState = {
  name: "",
  description: "",
  cadence: "weekly",
  custom_cadence_days: "7",
  send_day_of_week: "1",
  send_hour_utc: "9",
  llm_system_prompt: "",
  subject_template: "",
  topic_batch_size: "25",
  topic_refill_threshold: "5",
};

const DAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const fromNewsletter = (n: Newsletter): FormState => ({
  name: n.name,
  description: n.description ?? "",
  cadence: n.cadence,
  custom_cadence_days: n.custom_cadence_days?.toString() ?? "7",
  send_day_of_week: n.send_day_of_week?.toString() ?? "1",
  send_hour_utc: n.send_hour_utc.toString(),
  llm_system_prompt: n.llm_system_prompt,
  subject_template: n.subject_template,
  topic_batch_size: n.topic_batch_size?.toString() ?? "25",
  topic_refill_threshold: n.topic_refill_threshold?.toString() ?? "5",
});

const NewsletterForm: React.FC<NewsletterFormProps> = ({
  initial,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [values, setValues] = useState<FormState>(
    initial ? fromNewsletter(initial) : EMPTY
  );
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const { user } = useAuth();
  const [previewNotes, setPreviewNotes] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendTestLoading, setSendTestLoading] = useState(false);
  const [sendTestNotice, setSendTestNotice] = useState<
    { kind: "success" | "error"; message: string } | null
  >(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onCancel();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel, submitting]);

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!values.name.trim()) next.name = "Name is required";
    if (!values.subject_template.trim())
      next.subject_template = "Subject template is required";
    if (!values.llm_system_prompt.trim())
      next.llm_system_prompt = "System prompt is required";

    if (values.cadence === "weekly" || values.cadence === "biweekly") {
      const d = Number(values.send_day_of_week);
      if (!Number.isInteger(d) || d < 0 || d > 6)
        next.send_day_of_week = "Choose a day of week";
    }
    if (values.cadence === "custom") {
      const n = Number(values.custom_cadence_days);
      if (!Number.isInteger(n) || n < 1)
        next.custom_cadence_days = "Must be a positive integer";
    }
    const h = Number(values.send_hour_utc);
    if (!Number.isInteger(h) || h < 0 || h > 23)
      next.send_hour_utc = "Hour must be 0–23";

    const batch = Number(values.topic_batch_size);
    if (!Number.isInteger(batch) || batch < 1 || batch > 200)
      next.topic_batch_size = "Batch size must be 1–200";
    const threshold = Number(values.topic_refill_threshold);
    if (!Number.isInteger(threshold) || threshold < 0 || threshold > batch)
      next.topic_refill_threshold = "Threshold must be 0–batch size";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateNewsletterInput = {
      name: values.name.trim(),
      description: values.description.trim() || undefined,
      cadence: values.cadence,
      send_hour_utc: Number(values.send_hour_utc),
      llm_system_prompt: values.llm_system_prompt,
      subject_template: values.subject_template.trim(),
      topic_batch_size: Number(values.topic_batch_size),
      topic_refill_threshold: Number(values.topic_refill_threshold),
    };
    if (values.cadence === "weekly" || values.cadence === "biweekly") {
      payload.send_day_of_week = Number(values.send_day_of_week);
    }
    if (values.cadence === "custom") {
      payload.custom_cadence_days = Number(values.custom_cadence_days);
    }
    onSubmit(payload);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setValues((v) => ({ ...v, [key]: value }));
  };

  const canPreview =
    values.llm_system_prompt.trim().length > 0 &&
    values.subject_template.trim().length > 0 &&
    !previewLoading &&
    !submitting;

  const handlePreview = async () => {
    setPreviewError(null);
    setPreviewResult(null);
    setSendTestNotice(null);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const result = await newsletterAdminService.preview({
        llm_system_prompt: values.llm_system_prompt,
        subject_template: values.subject_template,
        notes: previewNotes.trim() || undefined,
      });
      setPreviewResult(result);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendPreviewTest = async () => {
    if (!previewResult) return;
    setSendTestNotice(null);
    setSendTestLoading(true);
    try {
      const result = await newsletterAdminService.sendPreviewTest(
        previewResult.subject,
        previewResult.html
      );
      if (result.success) {
        setSendTestNotice({
          kind: "success",
          message: `Test sent to ${user?.email ?? "your inbox"}.`,
        });
      } else {
        setSendTestNotice({
          kind: "error",
          message: result.error ?? "Mailgun returned an error",
        });
      }
    } catch (err) {
      setSendTestNotice({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to send test",
      });
    } finally {
      setSendTestLoading(false);
    }
  };

  const labelClass = "block text-sm font-medium text-gray-300 mb-2";
  const inputClass =
    "w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50";
  const errorClass = "mt-1 text-xs text-red-400";

  const modal = (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onCancel();
      }}
    >
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full mt-10 mb-10">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {initial ? "Edit Newsletter" : "Create Newsletter"}
          </h2>
          <button
            onClick={onCancel}
            disabled={submitting}
            aria-label="Close"
            className="text-gray-400 hover:text-white disabled:opacity-50"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label htmlFor="nl-name" className={labelClass}>
              Name <span className="text-red-400">*</span>
            </label>
            <input
              id="nl-name"
              type="text"
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={submitting}
              className={inputClass}
              placeholder="Weekly Product Digest"
            />
            {errors.name && <div className={errorClass}>{errors.name}</div>}
          </div>

          <div>
            <label htmlFor="nl-description" className={labelClass}>
              Description
            </label>
            <input
              id="nl-description"
              type="text"
              value={values.description}
              onChange={(e) => setField("description", e.target.value)}
              disabled={submitting}
              className={inputClass}
              placeholder="Internal note — what is this newsletter for?"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="nl-cadence" className={labelClass}>
                Cadence <span className="text-red-400">*</span>
              </label>
              <select
                id="nl-cadence"
                value={values.cadence}
                onChange={(e) =>
                  setField("cadence", e.target.value as NewsletterCadence)
                }
                disabled={submitting}
                className={inputClass}
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom (every N days)</option>
              </select>
            </div>

            <div>
              <label htmlFor="nl-hour" className={labelClass}>
                Send hour (UTC, 0–23) <span className="text-red-400">*</span>
              </label>
              <input
                id="nl-hour"
                type="number"
                min={0}
                max={23}
                value={values.send_hour_utc}
                onChange={(e) => setField("send_hour_utc", e.target.value)}
                disabled={submitting}
                className={inputClass}
              />
              {errors.send_hour_utc && (
                <div className={errorClass}>{errors.send_hour_utc}</div>
              )}
            </div>

            {(values.cadence === "weekly" || values.cadence === "biweekly") && (
              <div>
                <label htmlFor="nl-dow" className={labelClass}>
                  Day of week <span className="text-red-400">*</span>
                </label>
                <select
                  id="nl-dow"
                  value={values.send_day_of_week}
                  onChange={(e) => setField("send_day_of_week", e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                >
                  {DAY_LABELS.map((label, idx) => (
                    <option key={idx} value={idx}>
                      {label}
                    </option>
                  ))}
                </select>
                {errors.send_day_of_week && (
                  <div className={errorClass}>{errors.send_day_of_week}</div>
                )}
              </div>
            )}

            {values.cadence === "custom" && (
              <div>
                <label htmlFor="nl-custom" className={labelClass}>
                  Every N days <span className="text-red-400">*</span>
                </label>
                <input
                  id="nl-custom"
                  type="number"
                  min={1}
                  value={values.custom_cadence_days}
                  onChange={(e) => setField("custom_cadence_days", e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                />
                {errors.custom_cadence_days && (
                  <div className={errorClass}>{errors.custom_cadence_days}</div>
                )}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="nl-subject" className={labelClass}>
              Subject template <span className="text-red-400">*</span>
            </label>
            <input
              id="nl-subject"
              type="text"
              value={values.subject_template}
              onChange={(e) => setField("subject_template", e.target.value)}
              disabled={submitting}
              className={inputClass}
              placeholder="e.g. SendUpdates Weekly — {{date}}"
            />
            {errors.subject_template && (
              <div className={errorClass}>{errors.subject_template}</div>
            )}
          </div>

          <div>
            <label htmlFor="nl-prompt" className={labelClass}>
              Newsletter brief <span className="text-red-400">*</span>
            </label>
            <textarea
              id="nl-prompt"
              value={values.llm_system_prompt}
              onChange={(e) => setField("llm_system_prompt", e.target.value)}
              disabled={submitting}
              rows={8}
              className={`${inputClass} font-mono text-sm`}
              placeholder="Describe the audience, voice, themes, and goals. e.g. 'Weekly product digest for early-stage SaaS founders. Tone: punchy and pragmatic. Themes: pricing, GTM, product/UX, fundraising.'"
            />
            <p className="mt-2 text-xs text-gray-500">
              {initial
                ? "Editing this won't automatically rerun the topic plan. Use the “Regenerate plan” button on the detail page when you're ready."
                : "On save, we'll expand this into a distilled runtime prompt and a batch of upcoming topics. Topic generation can take 10–30s."}
            </p>
            {errors.llm_system_prompt && (
              <div className={errorClass}>{errors.llm_system_prompt}</div>
            )}
          </div>

          <details className="rounded-lg border border-gray-700/60 bg-gray-800/30 px-4 py-3">
            <summary className="cursor-pointer text-sm font-semibold text-gray-200">
              Advanced — topic queue
            </summary>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nl-batch" className={labelClass}>
                  Topics per generation
                </label>
                <input
                  id="nl-batch"
                  type="number"
                  min={1}
                  max={200}
                  value={values.topic_batch_size}
                  onChange={(e) => setField("topic_batch_size", e.target.value)}
                  disabled={submitting}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-gray-500">
                  How many topics the planner produces per run. Default 25.
                </p>
                {errors.topic_batch_size && (
                  <div className={errorClass}>{errors.topic_batch_size}</div>
                )}
              </div>
              <div>
                <label htmlFor="nl-threshold" className={labelClass}>
                  Refill threshold
                </label>
                <input
                  id="nl-threshold"
                  type="number"
                  min={0}
                  max={Number(values.topic_batch_size) || 200}
                  value={values.topic_refill_threshold}
                  onChange={(e) =>
                    setField("topic_refill_threshold", e.target.value)
                  }
                  disabled={submitting}
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Auto-refill kicks in when pending topics drop below this. Default 5.
                </p>
                {errors.topic_refill_threshold && (
                  <div className={errorClass}>
                    {errors.topic_refill_threshold}
                  </div>
                )}
              </div>
            </div>
          </details>

          <div className="border-t border-gray-700 pt-5">
            <label htmlFor="nl-preview-notes" className={labelClass}>
              Preview notes (optional)
            </label>
            <input
              id="nl-preview-notes"
              type="text"
              value={previewNotes}
              onChange={(e) => setPreviewNotes(e.target.value)}
              disabled={submitting || previewLoading}
              placeholder="e.g. Talking points the preview should include"
              className={inputClass}
            />
            <p className="mt-2 text-xs text-gray-500">
              Run a one-off LLM call to see what this newsletter will produce.
              Nothing is saved until you click {initial ? '"Save changes"' : '"Create"'}.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview}
              title={
                canPreview
                  ? undefined
                  : "Fill in system prompt and subject template first"
              }
              className="px-5 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 disabled:opacity-50 transition-all"
            >
              {previewLoading ? "Generating preview..." : "Preview with LLM"}
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="px-5 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 transition-all"
              >
                {submitting
                  ? initial
                    ? "Saving..."
                    : "Planning topics..."
                  : initial
                    ? "Save changes"
                    : "Create & plan topics"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {previewOpen && (
        <PreviewOverlay
          loading={previewLoading}
          error={previewError}
          result={previewResult}
          sendTestLoading={sendTestLoading}
          sendTestNotice={sendTestNotice}
          recipientEmail={user?.email ?? null}
          onClose={() => {
            if (previewLoading || sendTestLoading) return;
            setPreviewOpen(false);
            setSendTestNotice(null);
          }}
          onRegenerate={handlePreview}
          onSendTest={handleSendPreviewTest}
        />
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

interface PreviewOverlayProps {
  loading: boolean;
  error: string | null;
  result: PreviewResult | null;
  sendTestLoading: boolean;
  sendTestNotice: { kind: "success" | "error"; message: string } | null;
  recipientEmail: string | null;
  onClose: () => void;
  onRegenerate: () => void;
  onSendTest: () => void;
}

const PreviewOverlay: React.FC<PreviewOverlayProps> = ({
  loading,
  error,
  result,
  sendTestLoading,
  sendTestNotice,
  recipientEmail,
  onClose,
  onRegenerate,
  onSendTest,
}) => (
  <div
    className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
    onClick={(e) => {
      if (e.target === e.currentTarget && !loading) onClose();
    }}
  >
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-lg max-w-4xl w-full mt-10 mb-10">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <div>
          <h2 className="text-lg font-bold text-white">LLM preview</h2>
          <p className="text-xs text-gray-400 mt-1">
            Sample output from your prompt — nothing has been saved.
          </p>
        </div>
        <button
          onClick={onClose}
          disabled={loading}
          aria-label="Close preview"
          className="text-gray-400 hover:text-white disabled:opacity-50"
        >
          <CloseIcon width={20} height={20} />
        </button>
      </div>

      <div className="p-6 space-y-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3" />
            <p className="text-gray-400 text-sm">
              Calling the LLM — this may take 10–30s...
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
            <div className="text-red-300 text-sm font-semibold mb-1">
              Preview failed
            </div>
            <div className="text-red-200 text-xs font-mono">{error}</div>
          </div>
        )}

        {!loading && !error && result && (
          <>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Subject</div>
              <div className="text-white text-sm bg-gray-800/60 border border-gray-700 rounded px-3 py-2">
                {result.subject}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase mb-1">Body</div>
              <HtmlPreviewPane html={result.wrappedHtml} height="620px" />
            </div>
          </>
        )}

        {sendTestNotice && (
          <div
            className={
              sendTestNotice.kind === "success"
                ? "bg-green-900/20 border border-green-800/50 rounded-lg p-3 text-green-300 text-sm"
                : "bg-red-900/20 border border-red-800/50 rounded-lg p-3 text-red-300 text-sm"
            }
          >
            {sendTestNotice.message}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-gray-700">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onRegenerate}
            disabled={loading || sendTestLoading}
            className="px-4 py-2 bg-gray-600/20 text-gray-200 border border-gray-600/50 rounded-lg text-sm font-semibold hover:bg-gray-600/30 disabled:opacity-50 transition-all"
          >
            {loading ? "Generating..." : "Generate again"}
          </button>
          <button
            onClick={onSendTest}
            disabled={loading || sendTestLoading || !result}
            title={
              result
                ? `Send to ${recipientEmail ?? "your email"}`
                : "Generate a preview first"
            }
            className="px-4 py-2 bg-purple-600/20 text-purple-200 border border-purple-600/50 rounded-lg text-sm font-semibold hover:bg-purple-600/30 disabled:opacity-50 transition-all"
          >
            {sendTestLoading ? "Sending..." : "Send test to me"}
          </button>
        </div>
        <button
          onClick={onClose}
          disabled={loading || sendTestLoading}
          className="px-5 py-2 text-gray-300 hover:text-white transition-colors disabled:opacity-50"
        >
          Close preview
        </button>
      </div>
    </div>
  </div>
);

export default NewsletterForm;
